import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PlayCircle, Users, XCircle, CheckCircle2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { EmptyState, PageHeader, PageLoader, Panel, StatusBadge, DemoBanner } from "@/components/common";
import { WeeklyCalendar } from "@/components/WeeklyCalendar";
import { ViewToggle } from "@/components/ViewToggle";

async function startSession(s, refresh) {
  const win = window.open("", "_blank");
  try {
    const { data } = await api.post(`/sessions/${s.id}/start`);
    if (win) win.location.href = data.start_url; else window.location.href = data.start_url;
    toast.success("Meeting started — you are the host");
    refresh();
  } catch (e) { win?.close(); toast.error(errMsg(e)); }
}

function Participants({ session, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { setItems(null); if (session) api.get(`/sessions/${session.id}/participants`).then((r) => setItems(r.data)); }, [session]);
  return (
    <Dialog open={!!session} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="participants-dialog">
        <DialogHeader><DialogTitle>Participants · {session?.title}</DialogTitle><DialogDescription>Attendance is recorded when a student joins from FosterApp.</DialogDescription></DialogHeader>
        {!items ? <PageLoader /> : items.map((p) => (
          <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm" data-testid={`participant-${p.student_id}`}>
            <span className="font-semibold">{p.student_name}</span>
            <span className="flex gap-1"><StatusBadge status={p.attendance} /><StatusBadge status={p.credit_status} /></span>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export function TutorSessionTable({ items, refresh }) {
  const [parts, setParts] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [complete, setComplete] = useState(null);
  const act = async (url, body) => { try { const r = await api.post(url, body); toast.success(r.data.message); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const start = (s) => startSession(s, refresh);
  return (
    <div className="overflow-x-auto">
      <Table data-testid="tutor-sessions-table">
        <TableHeader><TableRow><TableHead>Session</TableHead><TableHead>Date & time</TableHead><TableHead>Students</TableHead><TableHead>Meeting</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{items.map((s) => (
          <TableRow key={s.id} data-testid={`tutor-session-row-${s.id}`}>
            <TableCell><p className="font-semibold text-[#0B1A33]">{s.title}</p><p className="text-xs text-slate-500">{s.subject} · {s.duration} min</p></TableCell>
            <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(s.start_at)}</TableCell>
            <TableCell className="text-sm">{s.student_names?.length}</TableCell>
            <TableCell><span className="font-mono text-xs">{s.meeting?.meeting_id}</span><span className="block text-[10px] uppercase text-slate-400">{s.meeting?.provider}</span></TableCell>
            <TableCell><StatusBadge status={s.status} testid={`tutor-session-status-${s.id}`} /></TableCell>
            <TableCell><div className="flex justify-end gap-2">
              {["scheduled", "in_progress"].includes(s.status) && <Button size="sm" onClick={() => start(s)} className="rounded-full bg-[#1F6FFF] hover:bg-[#1450C9]" data-testid={`start-meeting-button-${s.id}`}><PlayCircle className="h-4 w-4" />{s.status === "in_progress" ? "Rejoin" : "Start"}</Button>}
              {s.status === "in_progress" && <Button size="sm" onClick={() => setComplete(s)} className="rounded-full bg-[#3CC34A] text-[#0B1A33] hover:bg-[#2DA638]" data-testid={`complete-session-button-${s.id}`}><CheckCircle2 className="h-4 w-4" />End & complete</Button>}
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setParts(s)} data-testid={`view-participants-button-${s.id}`}><Users className="h-4 w-4" /></Button>
              {s.status === "scheduled" && <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => setCancel(s)} data-testid={`cancel-session-button-${s.id}`}><XCircle className="h-4 w-4" /></Button>}
            </div></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
      <Participants session={parts} onClose={() => setParts(null)} />
      <AlertDialog open={!!cancel} onOpenChange={(o) => !o && setCancel(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Cancel "{cancel?.title}"?</AlertDialogTitle>
          <AlertDialogDescription>All reserved credits will be released and students will be notified.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Keep session</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-session" onClick={() => act(`/sessions/${cancel.id}/cancel`, { reason: "Cancelled by tutor" })}>Cancel session</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!complete} onOpenChange={(o) => !o && setComplete(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Complete "{complete?.title}"?</AlertDialogTitle>
          <AlertDialogDescription>Students who joined will be charged 100 credits each. Credits for students who did not attend will be released.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction className="bg-[#3CC34A] text-[#0B1A33] hover:bg-[#2DA638]" data-testid="confirm-complete-session" onClick={() => act(`/sessions/${complete.id}/complete`)}>Complete session</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function TutorSessions({ scope }) {
  const [items, setItems] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [view, setView] = useState("calendar");
  const load = useCallback(() => api.get("/tutor/sessions", { params: { scope } }).then((r) => setItems(r.data)), [scope]);
  useEffect(() => { setItems(null); load(); }, [load]);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); }, []);
  return (
    <div>
      <PageHeader title={scope === "upcoming" ? "Upcoming Sessions" : "Session History"} subtitle={scope === "upcoming" ? "Start meetings as host, view participants or cancel." : "Completed and cancelled sessions."} />
      {scope === "upcoming" && cfg?.video_provider === "jitsi" && <div className="mb-4"><DemoBanner text="Meetings use free Jitsi Meet rooms (not Zoom) until Zoom credentials are configured" /></div>}
      {scope === "upcoming" && <div className="mb-4 flex justify-end"><ViewToggle view={view} setView={setView} /></div>}
      <Panel>{!items ? <PageLoader /> : scope === "upcoming" && view === "calendar" ? (
        <WeeklyCalendar items={items} subtitle={(s) => `${s.student_names?.length || 0} student(s) · ${s.subject}`} renderAction={(s) => (
          <Button size="sm" onClick={() => startSession(s, load)} data-testid={`calendar-start-button-${s.id}`}
            className="h-6 rounded-full bg-[#1F6FFF] px-2.5 text-[11px] hover:bg-[#1450C9]"><PlayCircle className="h-3 w-3" />{s.status === "in_progress" ? "Rejoin" : "Start"}</Button>
        )} />
      ) : items.length === 0 ? <div className="p-6"><EmptyState title="No sessions" /></div> : <TutorSessionTable items={items} refresh={load} />}</Panel>
    </div>
  );
}
